// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    let mounted = true
    
    const initializeAuth = async () => {
      try {
        // Get initial session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        
        if (mounted) {
          if (sessionError) {
            console.log('Session error:', sessionError)
            // If error, assume no user
            setUser(null)
            setUserProfile(null)
          } else if (session?.user) {
            console.log('Session found, user:', session.user.email)
            setUser(session.user)
            // Don't wait for profile fetch to complete - let it happen asynchronously
            fetchUserProfile(session.user).catch(error => {
              console.error('Profile fetch failed during init:', error)
            })
          } else {
            console.log('No session found')
            setUser(null)
            setUserProfile(null)
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
        if (mounted) {
          setUser(null)
          setUserProfile(null)
        }
      } finally {
        if (mounted) {
          setLoading(false)
          setInitialized(true)
        }
      }
    }

    initializeAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event)
      
      if (!mounted) return
      
      if (session?.user) {
        console.log('User authenticated:', session.user.email)
        setUser(session.user)
        // Don't block the auth state change for profile fetching
        fetchUserProfile(session.user).catch(error => {
          console.error('Profile fetch failed during state change:', error)
        })
      } else {
        console.log('User signed out')
        setUser(null)
        setUserProfile(null)
      }
      
      setLoading(false)
      // If auth state changes, we are definitely initialized
      setInitialized(true)
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  // Fetch profile function defined outside to be reusable
  const fetchUserProfile = async (authUser) => {
    if (!authUser) return null
    
    try {
      console.log('Fetching user profile for auth_user_id:', authUser.id)
      
      // Check if user profile exists in classroom.users
      const { data: profile, error } = await supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', authUser.id)
        .single()

      if (error) {
        console.log('Profile fetch error:', error)
        
        // If user doesn't exist, create one
        if (error.code === 'PGRST116') {
          console.log('User profile not found, creating new one...')
          
          const { data: newProfile, error: createError } = await supabase
            .from('users')
            .insert({
              auth_user_id: authUser.id,
              email: authUser.email,
              full_name: authUser.user_metadata?.full_name || authUser.email
            })
            .select()
            .single()
            
          if (createError) {
            console.error('Error creating user profile:', createError)
            // Don't throw error, just return null to prevent app from breaking
            return null
          } else {
            console.log('User profile created successfully:', newProfile)
            setUserProfile(newProfile)
            return newProfile
          }
        } else {
          console.error('Unexpected error fetching user profile:', error)
          // Don't throw error, just return null to prevent app from breaking
          return null
        }
      } else {
        console.log('User profile found:', profile)
        setUserProfile(profile)
        return profile
      }
    } catch (error) {
      console.error('Error in fetchUserProfile:', error)
      // Don't throw error, just return null to prevent app from breaking
      return null
    }
  }

  const signUp = async (email, password, fullName) => {
    setLoading(true)
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName
          }
        }
      })
      return { data, error }
    } catch (error) {
      console.error('Sign up error:', error)
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email, password) => {
    setLoading(true)
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      return { data, error }
    } catch (error) {
      console.error('Sign in error:', error)
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    setLoading(true)
    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        console.error('Sign out error:', error)
      }
      setUser(null)
      setUserProfile(null)
      return { error }
    } catch (error) {
      console.error('Sign out error:', error)
      return { error }
    } finally {
      setLoading(false)
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    initialized,
    signUp,
    signIn,
    signOut
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}